/**
 * useAbVariant - React hook for A/B testing
 *
 * Usage:
 *   const { variant, loading, trackConversion } = useAbVariant('landing_cta_test');
 *   if (loading) return <Skeleton />;
 *   const label = variant === 'variant_a' ? 'Generate a quote in 60s' : 'Start free trial';
 *   <Button onClick={() => trackConversion('signup')}>{label}</Button>
 */
import { useEffect, useState, useCallback } from 'react';
import { api } from './api';

// Stable session id for anonymous users (stored in localStorage)
function getAnonSubjectId() {
    let sid = localStorage.getItem('cantiere_ab_subject_id');
    if (!sid) {
        sid = 'anon-' + Math.random().toString(36).slice(2) + '-' + Date.now().toString(36);
        localStorage.setItem('cantiere_ab_subject_id', sid);
    }
    return sid;
}

export function useAbVariant(experimentKey, { fallback = 'control' } = {}) {
    const [variant, setVariant] = useState(fallback);
    const [loading, setLoading] = useState(true);
    const [subjectId, setSubjectId] = useState(null);

    useEffect(() => {
        if (!experimentKey) { setLoading(false); return; }
        const cacheKey = `ab_variant:${experimentKey}`;
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
            try {
                const parsed = JSON.parse(cached);
                setVariant(parsed.variant);
                setSubjectId(parsed.subject_id);
                setLoading(false);
                return;
            } catch (e) { /* fallthrough */ }
        }
        const anonSid = getAnonSubjectId();
        api.post('/ab/assign', { experiment_key: experimentKey, subject_id: anonSid })
            .then(res => {
                const v = res.data?.variant_key || fallback;
                const sid = res.data?.subject_id || anonSid;
                setVariant(v);
                setSubjectId(sid);
                sessionStorage.setItem(cacheKey, JSON.stringify({ variant: v, subject_id: sid }));
            })
            .catch(() => {
                setVariant(fallback);
                setSubjectId(anonSid);
            })
            .finally(() => setLoading(false));
    }, [experimentKey, fallback]);

    const trackConversion = useCallback((event = 'conversion', value = null) => {
        if (!experimentKey || !subjectId) return;
        // Skip duplicate conversion tracking per session
        const flag = `ab_converted:${experimentKey}:${event}`;
        if (sessionStorage.getItem(flag)) return;
        sessionStorage.setItem(flag, '1');
        api.post('/ab/conversion', {
            experiment_key: experimentKey,
            subject_id: subjectId,
            event,
            value,
        }).catch(() => { /* swallow */ });
    }, [experimentKey, subjectId]);

    return { variant, loading, trackConversion, subjectId };
}
